module.exports = {
    namespaced: true,
    state: {
        /**
         * The asset states on page load.  Never edited.
         */
        asset: {},

        /**
         * The dates selected on calendar.
         */
        selected_dates: {
            start_date: null,
            end_date: null,
        },

        /**
         * The dates being queried by the calendar (start of month, end of month)
         */
        calendar_dates: {
            start_date: null,
            end_date: null,
        },

        /**
         * New products being created on bundle calendar.  These are merged with events when calendar loads
         */
        products: {},
        saved_products: {},
        price_modifiers: {},

        /**
         * Assets to be included in bundle
         */
        bundle_assets: [],

        /**
         * Ids of assets to be included in bundle (for checkmarks)
         */
        bundle_selection: [],

        /**
         * Asset ids and units available by date.
         */
        asset_availability: [],

        suggested_price: 0,
    },

    /*
    |--------------------------------------------------------------------------
    | Mutations
    |--------------------------------------------------------------------------
    */
    mutations: {
        /*
        | Setters
        */
        setSelectedDates(state, dates) {
            state.selected_dates.start_date = _.get(dates, 'start_date');
            state.selected_dates.end_date = _.get(dates, 'end_date');
        },
        setCalendarDates(state, dates) {
            state.calendar_dates.start_date = _.get(dates, 'start_date');
            state.calendar_dates.end_date = _.get(dates, 'end_date');
        },
        setInitialAsset(state, asset) {
            Vue.set(state, 'asset', asset);
        },
        setAssets(state, assets) {
            state.bundle_assets = assets;
        },
        setPriceModifiers(state, payload) {
            state.price_modifiers = payload;
        },
        setSavedProducts(state, payload) {
            state.saved_products = payload;
        },
        setAssetAvailability(state, payload) {
            state.asset_availability = payload;
        },

        /*
        | Assets
        */
        addAsset(state, asset) {
            state.bundle_assets.push(asset);
        },
        removeAsset(state, asset_id) {
            state.bundle_assets = _.filter(state.bundle_assets, (asset) => {
                return asset.id !== asset_id;
            });
            state.bundle_selection = _.filter(state.bundle_selection, (id) => {
                return id !== asset_id;
            });
        },
        setCartItems(state, selection) {
            state.bundle_selection = selection;
        },
        setAssetUnits(state, payload) {
            _.each(state.bundle_assets, (bundleAsset, key) => {
                if (bundleAsset.id === payload.asset_id) {
                    Vue.set(state.bundle_assets[key].pivot, 'units', _.toInteger(payload.units));
                }
            });
        },

        /*
        | Products
        */
        setProducts(state, products) {
            state.products = products;
        },
        addProduct(state, payload) {
            let modifier = _.get(state.price_modifiers, dateTimeString(payload.start_date));
            let date = dateTimeString(payload.start_date);
            _.set(state.products, date, {
                editable: true,
                asset_id: payload.asset_id,
                start_date: date,
                end_date: date,
                base_price: payload.base_price,
                price: modifier && payload.base_price ? payload.base_price + modifier.multiplier * payload.base_price : payload.base_price,
                units_available: payload.units_available,
                units_posted: payload.units_posted,
                enabled: payload.enabled,
                note: _.get(payload, 'note'),
            });
        },
        setProductAttribute(state, { start_date, attribute, value }) {
            if (_.has(state.products, start_date)) {
                state.products[start_date][attribute] = value;
            }
        },
        setSuggestedPrice(state, price) {
            state.suggested_price = price;
        },
    },

    /*
    |--------------------------------------------------------------------------
    | Actions
    |--------------------------------------------------------------------------
    */
    actions: {
        setDefaultPromotionalProducts: (context) => {
            let start = dateTimeObject(context.state.asset.start_date);
            let end = dateTimeObject(context.state.asset.end_date);

            do {
                let date = dateTimeString(start);
                let prod = _.get(context.state.saved_products, date);
                let price = context.getters.dailySuggestedPrice(date);
                context.commit('addProduct', {
                    enabled: 1,
                    start_date: date,
                    end_date: date,
                    asset_id: context.state.asset.id,
                    base_price: price,
                    units_available: context.state.asset.base_units_available,
                    units_posted: context.state.asset.base_units_posted,
                    note: _.get(prod, 'note'), //todo fix notes for bundles
                });
            } while (start.add(1, 'days').diff(end) <= 0);
        },
    },

    /*
    |--------------------------------------------------------------------------
    | Getters
    |--------------------------------------------------------------------------
    */
    getters: {
        getProductAttribute: (state) => (payload) => {
            return _.get(state.products, payload.start_date + '.' + payload.attribute);
        },
        /*
        | Calendar events
        */
        getEvents: (state) => (formatter) => {
            let start = dateTimeObject(state.calendar_dates.start_date);
            let end = dateTimeObject(state.calendar_dates.end_date);
            let events = [];

            do {
                let date = dateTimeString(start);
                let event = formatter(
                    state,
                    start,
                    end,
                    _.get(state.products, date),
                    _.get(state.saved_products, date),
                    state.bundle_assets,
                    _.get(state.asset_availability, date),
                    _.get(state.price_modifiers, date),
                );
                if (event) {
                    events.push(event);
                }
            } while (start.add(1, 'days').diff(end) <= 0);

            return events;
        },

        /*
        | Bundle Dates
        */
        bundleDates: (state) => {
            return {
                start: _.get(state, 'asset.start_date', null),
                end: _.get(state, 'asset.end_date', null),
            };
        },

        /*
        | Assets
        */
        bundleSelection: (state) => {
            return state.bundle_selection;
        },
        bundleAssets: (state) => {
            return state.bundle_assets;
        },
        bundleAssetsCount: (state) => {
            return Object.keys(state.bundle_assets).length;
        },

        /*
        | Products
        */
        products: (state) => {
            return state.products;
        },
        allProducts: (state) => {
            return _.merge(state.saved_products, state.products);
        },
        productByDate: (state) => (date) => {
            return _.get(state.products, date);
        },

        /*
        | Misc
        */
        dailySuggestedPrice: (state) => (date) => {
            let avail = _.get(state.asset_availability, date);

            return _.sumBy(state.bundle_assets, (item) => {
                // e34e2512-b721-4e90-b453-479dd38736c5
                let unitsAvail = _.toNumber(_.get(avail, item.id, 0));
                let unitsRequired = _.toNumber(_.get(item, 'pivot.units', 0));
                let units = unitsAvail >= unitsRequired ? unitsRequired : 0;

                return _.toNumber(_.get(item, 'online_price', 0)) * units;
            });
        },
        suggestedPrice: (state) => {
            if (_.get(state.asset, 'asset_type') === 'gameday_bundle') {
                return _.sumBy(state.bundle_assets, (asset) => {
                    return _.get(asset, 'online_price', 0) * _.get(asset, 'pivot.units', 0);
                });
            }

            return state.suggested_price;
        },
    },
};
